import { ReactNode } from "react";
import { UseFormReturn } from "react-hook-form";

export type LoginFormData = {
  email: string;
  password: string;
};

export type Props = {
  /** Form instance from useForm */
  form: UseFormReturn<LoginFormData>;
  /** Handler for form submission */
  onLogin: () => void;
  /** Handler for Google login */
  onLoginWithGoogle: () => void;
  /** Whether to hide the signup link */
  hideSignupLink?: boolean;
  /** Render the form fields */
  renderForm: (props: { form: UseFormReturn<LoginFormData> }) => ReactNode;
  /** Render the action buttons */
  renderActions: (props: {
    isSubmitting: boolean;
    isValid: boolean;
    onLogin: () => void;
    onLoginWithGoogle: () => void;
  }) => ReactNode;
  /** Render additional content (links, etc.) */
  renderAdditionalContent?: (props: { hideSignupLink?: boolean }) => ReactNode;
  /** Container className */
  className?: string;
  /** Gap between sections (in pixels) */
  gap?: number;
};

/**
 * Base login form component - defines structure with render props.
 * Each app implements the render props with their own styling.
 *
 * @example
 * ```tsx
 * <LoginFormBase
 *   form={form}
 *   onLogin={handleSubmit}
 *   onLoginWithGoogle={googleMutation.mutateAsync}
 *   renderForm={({ form }) => (
 *     <Form form={form}>
 *       <FormField name="email" ... />
 *       <FormField name="password" ... />
 *     </Form>
 *   )}
 *   renderActions={({ isSubmitting, isValid, onLogin }) => (
 *     <Button onClick={onLogin} disabled={!isValid || isSubmitting}>
 *       Sign In
 *     </Button>
 *   )}
 * />
 * ```
 */
export function LoginFormBase({
  form,
  onLogin,
  onLoginWithGoogle,
  hideSignupLink,
  renderForm,
  renderActions,
  renderAdditionalContent,
  className,
  gap = 16,
}: Props) {
  const { isSubmitting, isValid } = form.formState;

  return (
    <div
      className={className}
      data-testid="login-form"
      style={{ display: "flex", flexDirection: "column", gap: `${gap}px` }}
    >
      {renderForm({ form })}
      {renderActions({ isSubmitting, isValid, onLogin, onLoginWithGoogle })}
      {renderAdditionalContent?.({ hideSignupLink })}
    </div>
  );
}

export default LoginFormBase;
